import { useState, useEffect } from 'react';

/**
 * Hook to check if user is authenticated for admin dashboard
 * Checks localStorage for admin authentication status (dev mode)
 * In production, this would check server-side sessions
 */
export const useAdminAuth = () => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAuth = () => {
      try {
        // In development mode, check localStorage for admin session
        const isDevMode = import.meta.env.DEV;
        const isDevAuthEnabled = isDevMode && 
          import.meta.env.VITE_ENABLE_ADMIN_DEV_AUTH === 'true';
        
        if (isDevAuthEnabled) {
          const devSession = localStorage.getItem('admin_dev_session');
          if (devSession) {
            try {
              const session = JSON.parse(devSession);
              const isAuthenticated = session.isAuthenticated && Date.now() < session.expiresAt;
              setIsAdminAuthenticated(isAuthenticated);
            } catch (parseError) {
              localStorage.removeItem('admin_dev_session');
              setIsAdminAuthenticated(false);
            }
          } else {
            setIsAdminAuthenticated(false);
          }
        } else {
          // In production, we would check server-side session
          // For now, assume not authenticated if not in dev mode
          setIsAdminAuthenticated(false);
        }
      } catch (error) {
        console.warn('Error checking admin auth:', error);
        setIsAdminAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminAuth();

    // Listen for storage changes (e.g., login/logout in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'admin_dev_session') {
        checkAdminAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return { isAdminAuthenticated, loading };
};